import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";

// user states: undefined (checking) | null (not authenticated) | object (authenticated)
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async ({ email, password }) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      // Special-case pending_deletion so the UI can redirect to /account/recovery
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "pending_deletion") {
        return {
          ok: false,
          pending_deletion: true,
          scheduled_at: detail.scheduled_at,
          error: "pending_deletion",
        };
      }
      return { ok: false, error: extractError(e, "Login failed.") };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: extractError(e, "Registration failed.") };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, isAuthenticated: !!user, isChecking: user === undefined, login, register, logout, refresh, setUser }),
    [user, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
